import { useCallback, useMemo } from 'react'
import { LocationData } from 'features/load-hub/types/filters'
import { readPerWindow, readPerWindowScoped, writePerWindow, writePerWindowScoped } from 'features/load-hub/utils/perWindowStorage'
import { toDate } from '../utils/format'
import { BOX_TRUCK_WORKSPACE_TTL_MS } from '../utils/boxTruckTabExpiration'
import { BoxTruckSearchFilters, capOrNull, EMPTY_FILTERS, legOptions, loadTypeOrBoth, sanitizePickup, sanitizePickupEnd } from './requestMapping'
import {
    BOX_TRUCK_DAT_SELECTION_VERSION,
    type BoxTruckCapacityLimits,
    migrateBoxTruckEquipmentLabel,
    restoreBoxTruckDatSubtypeCodes,
    sanitizeBoxTruckCapacityLimits,
    sanitizeBoxTruckDatSubtypeCodes,
    sanitizeBoxTruckMaxPostedAgeHours,
} from '@numeo/types-core'

const STORAGE_KEY = 'box_truck_filters'

/** The lane / radius / date / equipment filters, in a JSON-serializable shape (dates as ISO). Persisted so a
 *  page reload restores the last SEARCHED filters instead of resetting to anywhere-to-anywhere. */
interface PersistedBoxTruckFilters {
    origin: LocationData | null
    destination: LocationData | null
    originOptions?: LocationData[]
    destinationOptions?: LocationData[]
    originRadius: number | null
    destinationRadius: number | null
    startDate: string | null
    endDate: string | null
    equipmentLabels: string[]
    datEquipmentTypes?: string[]
    datEquipmentSelectionVersion?: number
    capacityLimits?: BoxTruckCapacityLimits
    includeUnknownCapacity?: boolean
    fullPartial?: string | null
    lengthFt?: number | null
    weightLbs?: number | null
    maxPostedAgeHours?: number | null
}

/** The subset of BoxTruckSearchFilters the user actually sets (everything but the transient numeoId find). */
type BoxTruckFilterInputs = Pick<
    BoxTruckSearchFilters,
    | 'origin'
    | 'destination'
    | 'originOptions'
    | 'destinationOptions'
    | 'originRadius'
    | 'destinationRadius'
    | 'startDate'
    | 'endDate'
    | 'equipmentLabels'
    | 'datEquipmentTypes'
    | 'capacityLimits'
    | 'includeUnknownCapacity'
    | 'fullPartial'
    | 'lengthFt'
    | 'weightLbs'
    | 'maxPostedAgeHours'
>

const readRawPersisted = (scopedOnly: boolean): BoxTruckSearchFilters => {
    try {
        const raw = scopedOnly ? readPerWindowScoped(STORAGE_KEY) : readPerWindow(STORAGE_KEY)
        if (!raw) return { ...EMPTY_FILTERS }
        const p = JSON.parse(raw) as PersistedBoxTruckFilters
        if (typeof p !== 'object' || p === null) return { ...EMPTY_FILTERS }
        return {
            origin: p.origin ?? null,
            destination: p.destination ?? null,
            originOptions: legOptions(p.originOptions, p.origin ?? null),
            destinationOptions: legOptions(p.destinationOptions, p.destination ?? null),
            originRadius: p.originRadius ?? null,
            destinationRadius: p.destinationRadius ?? null,
            startDate: toDate(p.startDate),
            endDate: toDate(p.endDate),
            // Migrate the stored pick onto the current vocabulary. A saved label that no longer exists does
            // not degrade to "no filter" — it matches no row and no DAT code, so the operator reopens the
            // board to an empty screen with a filter chip that looks perfectly normal.
            equipmentLabels: Array.isArray(p.equipmentLabels) ? p.equipmentLabels.map(migrateBoxTruckEquipmentLabel) : [],
            datEquipmentTypes: restoreBoxTruckDatSubtypeCodes(p.datEquipmentTypes, p.datEquipmentSelectionVersion),
            capacityLimits: sanitizeBoxTruckCapacityLimits(p.capacityLimits),
            includeUnknownCapacity: p.includeUnknownCapacity !== false,
            fullPartial: loadTypeOrBoth(p.fullPartial),
            lengthFt: capOrNull(p.lengthFt),
            weightLbs: capOrNull(p.weightLbs),
            maxPostedAgeHours: sanitizeBoxTruckMaxPostedAgeHours(p.maxPostedAgeHours),
            numeoId: null,
        }
    } catch (error) {
        console.warn('[box-truck] Could not restore persisted filters.', error instanceof Error ? error.message : 'Unknown storage error')
        return { ...EMPTY_FILTERS }
    }
}

/** Restored/seed filters with the required pickup window defaulted to today .. today+6 on a fresh board, while a
 *  restored last-search keeps its own dates (past ones snapped up to today). */
const readPersisted = (scopedOnly: boolean): BoxTruckSearchFilters => {
    const f = readRawPersisted(scopedOnly)
    return { ...f, startDate: sanitizePickup(f.startDate), endDate: sanitizePickupEnd(f.endDate) }
}

/**
 * Persist + restore the Box Truck loadboard filters across page reloads. `initialFilters` is read ONCE on mount
 * from localStorage and seeds the first tab's draft. A URL deep-link (Map "Find loads" / state "View table") is
 * handled separately by the page + useBoxTruckTabs, which opens a dedicated searched tab for it. `persistFilters`
 * is called on Search to store the last-searched set. Detached windows pass `scopedOnly` so neither reads nor
 * writes can touch the shared seed used by future normal windows. Best-effort — blocked storage just skips it.
 */
export const useBoxTruckFilterPersistence = (
    scopedOnly = false
): { initialFilters: BoxTruckSearchFilters; persistFilters: (filters: BoxTruckFilterInputs) => void } => {
    const initialFilters = useMemo(() => readPersisted(scopedOnly), [scopedOnly])

    const persistFilters = useCallback(
        (filters: BoxTruckFilterInputs) => {
            try {
                const payload: PersistedBoxTruckFilters = {
                    origin: filters.origin ?? null,
                    destination: filters.destination ?? null,
                    originOptions: filters.originOptions ?? [],
                    destinationOptions: filters.destinationOptions ?? [],
                    originRadius: filters.originRadius ?? null,
                    destinationRadius: filters.destinationRadius ?? null,
                    startDate: filters.startDate ? filters.startDate.toISOString() : null,
                    endDate: filters.endDate ? filters.endDate.toISOString() : null,
                    equipmentLabels: filters.equipmentLabels ?? [],
                    datEquipmentTypes: sanitizeBoxTruckDatSubtypeCodes(filters.datEquipmentTypes),
                    datEquipmentSelectionVersion: BOX_TRUCK_DAT_SELECTION_VERSION,
                    capacityLimits: sanitizeBoxTruckCapacityLimits(filters.capacityLimits),
                    includeUnknownCapacity: filters.includeUnknownCapacity !== false,
                    fullPartial: filters.fullPartial ?? 'BOTH',
                    lengthFt: filters.lengthFt ?? null,
                    weightLbs: filters.weightLbs ?? null,
                    // Written as well as read: the tab keeps the committed age, so dropping it here left the
                    // reloaded CONTROL on the default while the board searched on the stored one — the panel
                    // and the request disagreeing with nothing on screen to say which had won.
                    maxPostedAgeHours: sanitizeBoxTruckMaxPostedAgeHours(filters.maxPostedAgeHours),
                }
                const write = scopedOnly ? writePerWindowScoped : writePerWindow
                write(STORAGE_KEY, JSON.stringify(payload), BOX_TRUCK_WORKSPACE_TTL_MS)
            } catch (error) {
                console.warn('[box-truck] Could not persist filters.', error instanceof Error ? error.message : 'Unknown storage error')
            }
        },
        [scopedOnly]
    )

    return { initialFilters, persistFilters }
}
